/**
 * Pestaña Rendimiento: patrimonio neto con su curva de 12 barras, ROE, cap rate medio y el
 * ranking numerado de rentabilidad.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, queryKeys } from '../../api/client';
import { Card, CardHeader } from '../../components/Card';
import { BarChart } from '../../components/BarChart';
import { ProgressBar } from '../../components/ProgressBar';
import { ErrorState } from '../../components/EmptyState';
import { SkeletonCard } from '../../components/Skeleton';
import { formatEur, formatPercent, formatTrend } from '../../lib/format';
import styles from '../DashboardPage.module.css';

export function PerformanceTab() {
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.dashboardPerformance,
    queryFn: api.dashboardPerformance,
  });

  if (isPending) {
    return (
      <>
        <div className={styles.perfGrid}>
          <SkeletonCard rows={1} height={150} />
          <SkeletonCard rows={2} height={70} />
        </div>
        <SkeletonCard rows={4} height={46} />
      </>
    );
  }

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className={styles.perfGrid}>
        <Card variant="brand" className={styles.perfHero}>
          <div className={styles.perfHeroLabel}>Patrimonio neto total</div>
          <div className={styles.perfHeroValue}>{formatEur(data.netWorth)}</div>
          <div className={styles.perfHeroTrend}>
            {data.netWorthTrend.percent === null
              ? 'Sin histórico comparable'
              : `${formatTrend(data.netWorthTrend.percent)} en los últimos 12 meses`}
          </div>
          <BarChart
            variant="equity"
            showLabels={false}
            data={data.equityCurve.map((point) => ({
              key: point.month,
              heightPct: point.heightPct,
              title: `${point.label}: ${formatEur(point.equity)} de patrimonio`,
            }))}
          />
        </Card>

        <div className={styles.perfSide}>
          <Card variant="sm" className={styles.perfStat}>
            <div className={styles.perfStatLabel}>Retorno sobre capital (ROE)</div>
            <div className={styles.perfStatValue}>{formatPercent(data.roe)}</div>
            <div className={styles.perfStatNote}>
              ▲ mejor que el {data.roeBenchmarkPercentile}% de la zona
            </div>
          </Card>
          <Card variant="sm" className={styles.perfStat}>
            <div className={styles.perfStatLabel}>Cap rate medio</div>
            <div className={styles.perfStatValue}>{formatPercent(data.averageCapRate)}</div>
            <div className={`${styles.perfStatNote} ${styles.perfStatNoteMuted}`}>
              Media de {data.propertyCount} inmuebles
            </div>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader title="Ranking de rentabilidad" />
        {data.ranking.map((row) => (
          <button
            key={row.id}
            type="button"
            className={`${styles.compareRow} ${styles.rankRow}`}
            onClick={() => navigate(`/propiedades/${row.id}`)}
          >
            <div className={styles.rankBadge}>{row.rank}</div>
            <div className={styles.compareName}>
              <div className={styles.compareNameMain}>{row.name}</div>
              <div className={styles.compareNameSub}>{row.city}</div>
            </div>
            <ProgressBar
              percent={row.barPct}
              className={styles.rankBar}
              label={`Rentabilidad de ${row.name}`}
            />
            <div className={`${styles.compareValue} ${styles.rankValue}`}>
              {formatPercent(row.grossYield)}
            </div>
          </button>
        ))}
      </Card>
    </>
  );
}
